// ─────────────────────────────────────────────
//  Room conditions + provider handoff — pure domain (Phase 5.2).
//
//  • condition — a CRNA's operational note on their room (delayed start,
//    equipment issue…). SEPARATE field from the canonical room status
//    (empty·ready·active·closing·cancelled, C-002 in domain/status.js) —
//    a condition never changes a room's status.
//  • followUp — the provider-handoff chain between rooms. Origin shows
//    "Following to Room X", destination shows "Awaiting arrival from Room Y".
//    Driven by a `followUp` field on the room doc — never a sixth status.
//
//  Domain modules must not import the theme. Hex values mirror the token
//  theme's already-shipped badge tints (OverloadFlag / GoHomeAlert / Field
//  error / cancelled pill) per the 05.2 UI-SPEC color map.
// ─────────────────────────────────────────────

export const CONDITION_KINDS = [
  "on_time",
  "delayed_start",
  "equipment_issue",
  "patient_issue",
  "other",
];

export const NOTE_MAX = 200;

// icon glyphs are always paired with the text label (never icon-alone).
const META = {
  on_time:         { label: "On time",         icon: "✓", bg: "#dde6f1", border: "#b9c6d8", text: "#4a5280" },
  delayed_start:   { label: "Delayed start",   icon: "⏱", bg: "#fef8ec", border: "#f59e0b", text: "#7a4f00" },
  equipment_issue: { label: "Equipment issue", icon: "🔧", bg: "#fef2f2", border: "#dc2626", text: "#dc2626" },
  patient_issue:   { label: "Patient issue",   icon: "🩺", bg: "#f8f0fe", border: "#a855f7", text: "#7b2fbe" },
  other:           { label: "Other",           icon: "⋯", bg: "#dde6f1", border: "#b9c6d8", text: "#565d82" },
};

export const isConditionKind = (kind) => CONDITION_KINDS.includes(kind);

export function getConditionMeta(kind) {
  return META[kind] || META.other;
}

// Build the condition payload stored on the room doc. Throws on an unknown
// kind (callers present a fixed picker, so this only trips on programmer
// error). The note is operational-only (no PHI — C-008/D5), trimmed and
// hard-capped at NOTE_MAX characters.
export function makeCondition({ kind, note = "", setById = null, setByName = "" } = {}) {
  if (!isConditionKind(kind)) {
    throw new Error(`Unknown condition kind: ${kind}`);
  }
  return {
    kind,
    note: String(note || "").trim().slice(0, NOTE_MAX),
    setById: setById || null,
    setByName: setByName || "",
    setAt: new Date().toISOString(),
  };
}

// ── Follow-up / handoff ──────────────────────
// Origin + destination payload pair for a handoff. Both rooms carry a
// `followUp` field so each card can render its half of the chain without a
// join. `direction` tells the card which banner variant to draw.
export function makeFollowUpPair({
  providerId,
  providerName = "",
  fromRoomId,
  fromRoomName = "",
  toRoomId,
  toRoomName = "",
} = {}) {
  if (!providerId || !fromRoomId || !toRoomId) {
    throw new Error("makeFollowUpPair needs providerId, fromRoomId and toRoomId");
  }
  if (fromRoomId === toRoomId) {
    throw new Error("A follow-up must target a different room");
  }
  const setAt = new Date().toISOString();
  const base = { providerId, providerName: providerName || "", setAt };
  return {
    origin: { ...base, direction: "following", toRoomId, toRoomName: toRoomName || "" },
    destination: { ...base, direction: "awaiting", fromRoomId, fromRoomName: fromRoomName || "" },
  };
}

// Banner variant for a room's followUp field: "following" | "awaiting" | null.
export function followUpVariant(followUp) {
  if (!followUp || typeof followUp !== "object") return null;
  return followUp.direction === "following" || followUp.direction === "awaiting"
    ? followUp.direction
    : null;
}

// The handoff log entry appended (arrayUnion) onto the DESTINATION room when
// arrival is marked — the persisted trail that keeps the provider's room chain
// visible for the rest of the shift.
export function makeHandoffEntry({ followUp, toRoomId, toRoomName = "", dateKey } = {}) {
  if (!followUp || followUp.direction !== "awaiting") {
    throw new Error("makeHandoffEntry needs the destination room's awaiting followUp");
  }
  return {
    providerId: followUp.providerId || null,
    providerName: followUp.providerName || "",
    fromRoomId: followUp.fromRoomId || null,
    fromRoomName: followUp.fromRoomName || "",
    toRoomId: toRoomId || null,
    toRoomName: toRoomName || "",
    dateKey: dateKey || "",
    at: new Date().toISOString(),
  };
}

// ── Room chain for the shift ─────────────────
// Derive the provider's room chain (["OR 2", "OR 5", …]) for TODAY from:
//   1. persisted handoff entries on room docs (arrivals already made), and
//   2. the LIVE followUp pair (a hop set up but not yet arrived).
// Returns an ordered list of room-name steps, deduped consecutively; [] when
// the provider has no chain today (single room, nothing to show).
export function roomChainForProvider(rooms = [], providerId, dateKey) {
  if (!providerId) return [];
  const hops = [];
  for (const r of rooms) {
    for (const h of r?.handoffs || []) {
      if (h.providerId === providerId && (!dateKey || h.dateKey === dateKey)) {
        hops.push({ from: h.fromRoomName, to: h.toRoomName || r.name, at: h.at || "" });
      }
    }
  }
  hops.sort((a, b) => String(a.at).localeCompare(String(b.at)));
  // Live (not-yet-arrived) hop rides at the end of the chain.
  const liveOrigin = rooms.find(
    (r) => r?.followUp?.direction === "following" && r.followUp.providerId === providerId
  );
  if (liveOrigin) {
    hops.push({
      from: liveOrigin.name,
      to: liveOrigin.followUp.toRoomName || "?",
      at: "￿", // sorts last
    });
  }
  if (hops.length === 0) return [];
  const steps = [];
  for (const h of hops) {
    if (steps.length === 0) steps.push(h.from || "?");
    if (steps[steps.length - 1] !== (h.from || "?") && (h.from || "?") !== steps[steps.length - 1]) {
      // chain break (data gap) — still append the hop's origin so nothing is lost
      steps.push(h.from || "?");
    }
    steps.push(h.to || "?");
  }
  // collapse consecutive duplicates
  return steps.filter((s, i) => i === 0 || s !== steps[i - 1]);
}
